import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { ImageOutlined as ImageIcon } from '@mui/icons-material';

interface ImagePreviewProps {
    uuid: string;
    alt: string;
    height?: number;
    updatedAt?: string;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ uuid, alt, height = 150, updatedAt }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const loadImage = async () => {
            try {
                const token = localStorage.getItem('token');
                // Add cache buster to force reload when version changes
                const cacheBuster = updatedAt ? `?t=${new Date(updatedAt).getTime()}` : '';
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/preview/${uuid}${cacheBuster}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to load image');
                }

                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                setImageUrl(url);
                setLoading(false);
            } catch (err) {
                setError(true);
                setLoading(false);
            }
        };

        loadImage();

        // Cleanup
        return () => {
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }
        };
    }, [uuid, updatedAt]);

    if (loading) {
        return (
            <Box
                sx={{
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100'
                }}
            >
                <CircularProgress size={30} />
            </Box>
        );
    }

    if (error || !imageUrl) {
        return (
            <Box
                sx={{
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100'
                }}
            >
                <ImageIcon sx={{ fontSize: 60, color: 'grey.400' }} />
            </Box>
        );
    }

    return (
        <img
            src={imageUrl}
            alt={alt}
            style={{
                width: '100%',
                height,
                objectFit: 'cover'
            }}
        />
    );
};

export default ImagePreview;
